<template>
  <div
    class="dc-card"
    :class="toneClass"
  >
    <div class="dc-card__head">
      <div class="dc-card__title">{{ $t('debtorCreditorTitle') }}</div>
    </div>
    <div
      v-if="canSee"
      class="dc-card__body"
    >
      <div
        v-if="hasData"
        class="dc-card__chart"
      >
        <apexchart
          type="donut"
          width="168"
          height="168"
          :options="chartOptions"
          :series="series"
        />
      </div>
      <div
        v-if="hasData"
        class="dc-card__legend"
      >
        <div
          v-for="row in rows"
          :key="row.label"
          class="dc-card__legend-row"
        >
          <span
            class="dc-card__legend-label"
            :style="{ color: row.color }"
          >
            {{ row.label }}
          </span>
          <span
            class="dc-card__legend-value"
            :style="{ color: row.color }"
          >
            <span class="dc-card__legend-num">{{ $numberFormat(row.amount) }}</span>
            {{ $store.state.currency.name }}
          </span>
        </div>
      </div>
      <div
        v-else-if="!loading"
        class="dc-card__empty"
      >
        <home-empty-state />
      </div>
      <v-progress-circular
        v-if="loading"
        :width="3"
        color="blue"
        indeterminate
        size="22"
      />
    </div>
    <div
      v-else
      class="dc-card__empty"
    >
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from './HomeEmptyState.vue'

const COLORS = ['#FF5252', '#4CAF50']

export default {
  name: 'HomeDebtorCreditor',
  components: { HomeEmptyState },
  data () {
    return {
      debtorsCount: 0,
      creditorsCount: 0,
      debtorsTotal: 0,
      creditorsTotal: 0,
      loading: false,
      loaded: false
    }
  },
  computed: {
    canSee () {
      return this.$store.getters.getpermission && this.$store.getters.getpermission.includes('overview')
    },
    hasData () {
      return this.loaded && (this.debtorsCount || this.creditorsCount)
    },
    toneClass () {
      if (!this.hasData) return ''
      const debtor = Math.abs(Number(this.debtorsTotal) || 0)
      const creditor = Math.abs(Number(this.creditorsTotal) || 0)
      if (debtor > creditor) return 'dc-card--debtor'
      if (creditor > debtor) return 'dc-card--creditor'
      return ''
    },
    series () {
      return [Number(this.debtorsCount) || 0, Number(this.creditorsCount) || 0]
    },
    rows () {
      return [
        {
          label: this.$t('debtor'),
          color: COLORS[0],
          amount: this.debtorsTotal
        },
        {
          label: this.$t('creditor'),
          color: COLORS[1],
          amount: this.creditorsTotal
        }
      ]
    },
    chartOptions () {
      return {
        chart: {
          type: 'donut',
          fontFamily: 'IRANSans',
          toolbar: { show: false },
          animations: { enabled: false },
          parentHeightOffset: 0,
          sparkline: { enabled: true }
        },
        labels: this.rows.map(row => row.label),
        colors: COLORS,
        legend: { show: false },
        stroke: {
          width: 2,
          colors: ['#fff']
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontWeight: 700,
            colors: ['#fff']
          },
          dropShadow: {
            enabled: true,
            color: '#111',
            top: 0,
            left: 0,
            blur: 2,
            opacity: 0.25
          },
          formatter (val) {
            if (val < 6) return ''
            return (Math.round(val * 10) / 10) + '٪'
          }
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: { size: '62%' },
            dataLabels: {
              offset: 0,
              minAngleToShowLabel: 18
            }
          }
        },
        tooltip: {
          y: {
            formatter (value) {
              return value
            }
          }
        }
      }
    }
  },
  mounted () {
    this.fetchList()
  },
  methods: {
    fetchList () {
      this.loading = true
      this.$axios.get(this.$endpoint + 'customerreport', {
        params: { select: 'customersBalanceStatus' }
      }).then(res => {
        const data = res.data || {}
        this.debtorsCount = data.debtorsCount || 0
        this.creditorsCount = data.creditorsCount || 0
        this.debtorsTotal = data.debtorsTotal || 0
        this.creditorsTotal = data.creditorsTotal || 0
        this.loaded = true
      }).catch(() => {
        this.loaded = true
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.dc-card {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px;
  height: 262px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.dc-card--debtor {
  background: #FFF8F7;
  border-color: #F3E2E0;
}
.dc-card--creditor {
  background: #F6FBF7;
  border-color: #E3F0E7;
}
.dc-card__head {
  flex-shrink: 0;
  margin-bottom: 4px;
}
.dc-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.dc-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.dc-card__chart {
  width: 100%;
  height: 168px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dc-card__chart ::v-deep .vue-apexcharts,
.dc-card__chart ::v-deep .apexcharts-canvas {
  margin: 0 auto;
}
.dc-card__legend {
  width: 100%;
  margin-top: auto;
  display: flex;
  flex-direction: column;
}
.dc-card__legend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
}
.dc-card__legend-row + .dc-card__legend-row {
  border-top: 1px solid #E5E7EB;
}
.dc-card__legend-label {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.dc-card__legend-value {
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.dc-card__legend-num {
  direction: ltr;
}
.dc-card__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6B7280;
  text-align: center;
}
</style>
